import {
  CartItemTotalDetails,
  CartTotalDetails,
  NonIncludedTax,
  round,
} from '@kouto/cart-prices';
import { ICartExperience } from 'types/cart';
import { CartPriceBooking } from 'types/cartPricing';
import {
  deriveLineTaxBreakdown,
  groupPricedUnitsByTier,
  ServerCartQuote,
  UNATTRIBUTED_TAX_ID,
} from './serverCartQuote';

/** A cart item's figures, carrying the server's quoted line when the server priced it. */
export type CartItemTotalDetailsWithQuote = CartItemTotalDetails & {
  serverQuote?: CartPriceBooking;
};

export type CartTotalDetailsWithQuotes = Omit<
  CartTotalDetails,
  'cartItemsDetails'
> & {
  cartItemsDetails: Record<string, CartItemTotalDetailsWithQuote>;
};

/** The shape a listing needs for a tax id to be given its name. */
export type TaxNameListing = {
  resourceGroups?:
    | {
        experiences?: { taxes?: TaxNameSource[] | null }[] | null;
      }[]
    | null;
  taxes?: TaxNameSource[] | null;
};

type TaxNameSource = { id?: string | null; name?: string | null };

type MergeOptions = {
  /** Shown for tax the quote could not attribute to a named tax. */
  fallbackTaxName: string;
};

const collectTaxNames = (
  listings: Record<string, TaxNameListing>,
): Record<string, string> =>
  Object.values(listings)
    .flatMap((listing) => [
      ...(listing.taxes ?? []),
      ...(listing.resourceGroups ?? []).flatMap((group) =>
        (group.experiences ?? []).flatMap(
          (experience) => experience.taxes ?? [],
        ),
      ),
    ])
    .reduce<Record<string, string>>(
      (names, tax) =>
        tax.id && tax.name ? { ...names, [tax.id]: tax.name } : names,
      {},
    );

const toNonIncludedTaxes = (
  breakdown: Record<string, number>,
  taxNames: Record<string, string>,
  fallbackTaxName: string,
): NonIncludedTax[] =>
  Object.entries(breakdown).reduce<NonIncludedTax[]>(
    (taxes, [taxId, amount]) => {
      const name =
        taxId === UNATTRIBUTED_TAX_ID
          ? fallbackTaxName
          : taxNames[taxId] ?? fallbackTaxName;
      const existing = taxes.find((tax) => tax.name === name);
      if (!existing) {
        return [...taxes, { amountFee: amount, name }];
      }
      return taxes.map((tax) =>
        tax === existing
          ? { ...tax, amountFee: round(tax.amountFee + amount) }
          : tax,
      );
    },
    [],
  );

const mergeNonIncludedTaxes = (
  local: NonIncludedTax[],
  quoted: NonIncludedTax[],
): NonIncludedTax[] =>
  quoted.reduce<NonIncludedTax[]>((taxes, quotedTax) => {
    const existing = taxes.find((tax) => tax.name === quotedTax.name);
    if (!existing) {
      return [...taxes, quotedTax];
    }
    return taxes.map((tax) =>
      tax === existing
        ? { ...tax, amountFee: round(tax.amountFee + quotedTax.amountFee) }
        : tax,
    );
  }, local);

const sumAddOns = (
  booking: CartPriceBooking,
  field: 'discountAmount' | 'subtotal' | 'total',
): number =>
  booking.listingAddOns.reduce((sum, addOn) => sum + addOn[field], 0);

/** A continuous cart item's figures, read off the line the server quoted for it. */
export const buildQuotedCartItemDetails = (
  booking: CartPriceBooking,
  taxNames: Record<string, string>,
  fallbackTaxName: string,
): CartItemTotalDetailsWithQuote => {
  const totalAddOnsAmount = round(sumAddOns(booking, 'subtotal'));
  // What the coupon takes off the line, as the charge will: the booking's own and its add-ons'.
  const priceTiersDiscountAmount = round(booking.discountAmount);
  const addOnsDiscountAmount = round(sumAddOns(booking, 'discountAmount'));
  const nonIncludedTaxes = toNonIncludedTaxes(
    deriveLineTaxBreakdown(booking),
    taxNames,
    fallbackTaxName,
  );

  return {
    addOnsDiscountAmount,
    fixedAmountDiscount: 0,
    fixedExperienceAmountDiscount: 0,
    fixedPercentageDiscount: 0,
    includedAddOnsTaxes: [],
    includedPriceTiersTaxes: [],
    includedTaxes: [],
    isPrivateBooking: false,
    nonIncludedAddOnsTaxes: [],
    nonIncludedPriceTiersTaxes: nonIncludedTaxes,
    nonIncludedTaxes,
    percentageDiscountByCode: 0,
    priceTiersDiscountAmount,
    priceTiersTaxes: [],
    selectedPriceTiers: groupPricedUnitsByTier(booking).map((tier) => ({
      name: tier.name,
      price: tier.quantity > 0 ? round(tier.amount / tier.quantity) : 0,
      selectedNumber: tier.quantity,
    })),
    serverQuote: booking,
    subTotal: round(booking.subtotal + totalAddOnsAmount),
    total: round(booking.total + sumAddOns(booking, 'total')),
    totalAddOnsAmount,
    totalDiscountAmount: round(priceTiersDiscountAmount + addOnsDiscountAmount),
    totalPriceTiersAmount: booking.subtotal,
  };
};

/** Zeroed figures for a continuous item whose quote has not arrived, so the summary can still render. */
const buildPendingCartItemDetails = (): CartItemTotalDetailsWithQuote => ({
  addOnsDiscountAmount: 0,
  fixedAmountDiscount: 0,
  fixedExperienceAmountDiscount: 0,
  fixedPercentageDiscount: 0,
  includedAddOnsTaxes: [],
  includedPriceTiersTaxes: [],
  includedTaxes: [],
  isPrivateBooking: false,
  nonIncludedAddOnsTaxes: [],
  nonIncludedPriceTiersTaxes: [],
  nonIncludedTaxes: [],
  percentageDiscountByCode: 0,
  priceTiersDiscountAmount: 0,
  priceTiersTaxes: [],
  selectedPriceTiers: [],
  subTotal: 0,
  total: 0,
  totalAddOnsAmount: 0,
  totalDiscountAmount: 0,
  totalPriceTiersAmount: 0,
});

/** One continuous item's figures from the quote, or zeroed figures while its line has not arrived. */
export const deriveQuotedItemDetails = (
  booking: CartPriceBooking | undefined,
  listings: Record<string, TaxNameListing>,
  { fallbackTaxName }: MergeOptions,
): CartItemTotalDetailsWithQuote =>
  booking
    ? buildQuotedCartItemDetails(
        booking,
        collectTaxNames(listings),
        fallbackTaxName,
      )
    : buildPendingCartItemDetails();

/**
 * The locally priced cart with the server's quote for the continuous items folded in.
 *
 * `local` must have been computed over the non-continuous items only; every item in `continuousItems`
 * gets its figures from the quote instead. While the quote is null (in flight, or failed) the
 * continuous items contribute nothing, so the summary shows the locally priced portion rather than
 * throwing.
 *
 * The coupon is quoted with the continuous items, so what it takes off them is added to the discount
 * the local calculator found for the rest. Credit codes are still the local calculator's: they come off
 * the locally priced total only, so the total shown is never below what will be charged.
 */
export const mergeServerQuoteIntoCartTotals = (
  local: CartTotalDetails,
  quote: ServerCartQuote | null,
  continuousItems: Record<string, ICartExperience>,
  listings: Record<string, TaxNameListing>,
  { fallbackTaxName }: MergeOptions,
): CartTotalDetailsWithQuotes => {
  const taxNames = collectTaxNames(listings);

  const quotedItemsDetails = Object.values(continuousItems).reduce<
    Record<string, CartItemTotalDetailsWithQuote>
  >((details, item) => {
    const booking = quote?.lines[item.cartItemId];
    return {
      ...details,
      [item.cartItemId]: booking
        ? buildQuotedCartItemDetails(booking, taxNames, fallbackTaxName)
        : buildPendingCartItemDetails(),
    };
  }, {});

  // Summed from the lines of the items actually in the cart rather than read off the quote's own
  // totals: with `keepPreviousData` the quote can briefly describe a cart that has since changed.
  const quotedDetails = Object.values(quotedItemsDetails);

  return {
    ...local,
    cartItemsDetails: { ...local.cartItemsDetails, ...quotedItemsDetails },
    cartSubTotal: round(
      quotedDetails.reduce(
        (sum, details) => sum + details.subTotal,
        local.cartSubTotal,
      ),
    ),
    cartTotal: round(
      quotedDetails.reduce(
        (sum, details) => sum + details.total,
        local.cartTotal,
      ),
    ),
    totalDiscountAmount: round(
      quotedDetails.reduce(
        (sum, details) => sum + details.totalDiscountAmount,
        local.totalDiscountAmount,
      ),
    ),
    nonIncludedTaxes: quotedDetails.reduce(
      (taxes, details) =>
        mergeNonIncludedTaxes(taxes, details.nonIncludedTaxes),
      local.nonIncludedTaxes,
    ),
  };
};
